import React, { ReactElement, useEffect, useRef, useState } from "react";
import moment from "moment";
import { injectIntl, intlShape } from "@ai360/core/intl";
import { FieldAPI } from "@ai360/core";
import { DialogBox, DialogBoxFooterType, Loader, SelectInput, Section, Checkbox } from "~/core";
import { actions as picklistActions, selectors as picklistSelectors } from "~/core/picklist";
import { getPickListCode, PICKLIST_CROPPING_SEASON } from "~/core/picklist/picklist-names";
import { useAppDispatch, useAppSelector } from "~/store/hooks";
import { messages } from "../../../i18n-messages";
import "./export-event-summary.css";
import { toggleExportEventSummaryModal } from "../../actions";
import { IExportEventSummaryRequest } from "../../interfaces";

type ExportEventSummaryModalProps = {
    onAction: (exportEventSummaryRequest: IExportEventSummaryRequest) => void;
    intl: intlShape;
};

const ExportEventSummaryModal = ({
    onAction,
    intl: { formatMessage },
}: ExportEventSummaryModalProps): ReactElement => {
    const dispatch = useAppDispatch();
    const seasons = useAppSelector(
        (state) =>
            picklistSelectors.getPicklistOptionsFromCode(
                state,
                getPickListCode(PICKLIST_CROPPING_SEASON)
            ) as FieldAPI.ISeason[]
    );
    const [selectedSeason, setSelectedSeason] = useState<FieldAPI.ISeason>(null);
    const picklistRequestMade = useRef(false);
    const [eventTypesDisplay, setEventTypesDisplay] = useState({
        EventType: {
            displayName: "Event Type",
            show: true,
            types: {
                application: { displayName: "Application", show: true },
                harvest: { displayName: "Harvest", show: true },
                irrigation: { displayName: "Irrigation", show: true },
                planting: { displayName: "Planting", show: true },
                tillage: { displayName: "Tillage", show: true },
            },
        },
    });

    useEffect(() => {
        if ((!seasons || seasons.length === 0) && !picklistRequestMade.current) {
            picklistRequestMade.current = true;
            dispatch(
                picklistActions.fetchPicklistData({
                    [PICKLIST_CROPPING_SEASON]: getPickListCode(PICKLIST_CROPPING_SEASON),
                })
            );
        }
    }, [seasons, dispatch]);

    useEffect(() => {
        if (seasons && seasons.length > 0 && !selectedSeason) {
            const defaultSeasonValue = getCurrentYearAsValue(seasons);
            if (defaultSeasonValue) {
                setSelectedSeason(defaultSeasonValue);
            }
        }
    }, [seasons, selectedSeason]);

    const updateExportEventSummaryRequest = () => {
        const exportEventSummaryRequest = {
            croppingSeasonGuid: selectedSeason.value,
            croppingSeasonName: selectedSeason.label,
            eventTypes: Object.keys(eventTypesDisplay.EventType.types).filter(
                (k) => eventTypesDisplay.EventType.types[k].show
            ),
        };
        onAction(exportEventSummaryRequest);
        closeModal();
    };

    const getCurrentYearAsValue = (seasons: FieldAPI.ISeason[]): FieldAPI.ISeason => {
        const currentYear = moment(new Date(), "DD/MM/YYYY").year();
        const selectedSeason =
            seasons && seasons.filter((season) => season.label === `${currentYear}`);
        return selectedSeason.length > 0 ? selectedSeason[0] : null;
    };

    const onSeasonChange = (value: string) => {
        const selectedSeason = seasons.find((s) => s.value === value);
        setSelectedSeason(selectedSeason);
    };

    const closeModal = () => dispatch(toggleExportEventSummaryModal(false));

    const hasSomeEventTypesSelected = Object.keys(eventTypesDisplay.EventType.types).some(
        (k) => eventTypesDisplay.EventType.types[k].show
    );

    const hasAllEventTypesSelected = Object.keys(eventTypesDisplay.EventType.types).every(
        (k) => eventTypesDisplay.EventType.types[k].show
    );

    const actionDisabled = !selectedSeason || !hasSomeEventTypesSelected;

    const showLoader = !seasons || seasons.length === 0;

    return (
        <DialogBox
            action={formatMessage(messages.export)}
            actionDisabled={actionDisabled}
            draggable={true}
            unrestricted
            footerType={DialogBoxFooterType.ACTION_CANCEL}
            isOpen={true}
            onAction={updateExportEventSummaryRequest}
            onClose={closeModal}
            title={formatMessage(messages.exportingEventSummaryModalTitle)}
            className="export-event-summary-modal"
            testId="export-event-summary-modal"
        >
            <div className="batch-edit-form">
                <div className="batch-edit-events-group">
                    <SelectInput
                        required
                        clearable={false}
                        placeholderText={formatMessage(messages.exportEventSummarySeason)}
                        options={seasons}
                        value={selectedSeason?.value}
                        onChange={(value) => onSeasonChange(value)}
                        testId="export-event-summary-season-select"
                    />
                </div>
                <Section
                    className="event-type-options"
                    testId="export-event-summary-event-type-section"
                >
                    {Object.keys(eventTypesDisplay).map((rk) => (
                        <div className="report-page-selection-report" key={rk}>
                            <Checkbox
                                someChecked={hasSomeEventTypesSelected && !hasAllEventTypesSelected}
                                label={eventTypesDisplay[rk].displayName}
                                value={hasSomeEventTypesSelected}
                                onChange={(e, value) => {
                                    const updated = { ...eventTypesDisplay };
                                    updated[rk].show = value;
                                    Object.keys(updated[rk].types).forEach((pk) => {
                                        updated[rk].types[pk].show = value;
                                    });
                                    setEventTypesDisplay(updated);
                                }}
                                testId={`export-event-summary-event-type-checkbox-${rk}`}
                            />
                            <div className="report-page-selection-pages">
                                {Object.keys(eventTypesDisplay[rk].types).map((pk) => (
                                    <Checkbox
                                        key={pk}
                                        label={eventTypesDisplay[rk].types[pk].displayName}
                                        value={eventTypesDisplay[rk].types[pk].show}
                                        onChange={(e, value) => {
                                            const updated = { ...eventTypesDisplay };
                                            updated[rk].types[pk].show = value;
                                            if (value) {
                                                updated[rk].show = true;
                                            }
                                            setEventTypesDisplay(updated);
                                        }}
                                        testId={`export-event-summary-event-type-checkbox-${rk}-${pk}`}
                                    />
                                ))}
                            </div>
                        </div>
                    ))}
                </Section>
            </div>
            {showLoader && <Loader className="batch-edit-events-loader" />}
        </DialogBox>
    );
};

ExportEventSummaryModal.propTypes = {
    intl: intlShape,
};

export default injectIntl(ExportEventSummaryModal);
